import React, { useContext, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { LanguageContext } from "@lib/i18n/LanguageProvider";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import { CustomCheckbox, CustomForm, CustomModal } from "albinasoft-ui-package";
import { ElementType } from "albinasoft-ui-package/dist/components/CustomForm";
import {
  ClientTrackRequest,
  ClientTrackResponse,
  FormDetailResponse,
  SaveFormDataRequest,
} from "@models/ClientTrackModels";
import ClientTrackApi from "api/ClientTrackApi";
import { ResponseCode } from "@models/ApiBaseResponse";
import { GlobalOperations } from "config/GlobalApiOperations";

const onNotify = {
  success: (m: string) => toast.success(m),
  error: (m: string) => toast.error(m),
  warning: (m: string) => toast.warning(m),
};

// Layout context types
type LayoutContextType = {
  setLayoutContext: (title: string) => void;
  setLayoutHeaderImage: (url: string) => void;
  setPageTitle: (pageTitle: string) => void;
  setPageSummary: (pageSummary: string) => void;
  setVideoHelpUrl: (helpUrl: string) => void;
  setShowHeader: (showHeader: boolean) => void;
  setShowSidebar: (showSidebar: boolean) => void;
  setShowSummaryOnMobile: (showSummaryOnMobile: boolean) => void;
};

// API’den gelecek yapı
interface PageConfig {
  rawFormElements: Array<Record<string, any>>;
  initialValues: Record<string, any>;
  confirmLabel: string;
}

const RequestCreationForm: React.FC = () => {
  const {
    setPageTitle,
    setPageSummary,
    setVideoHelpUrl,
    setShowHeader,
    setShowSidebar,
    setShowSummaryOnMobile,
    setLayoutHeaderImage,
  } = useOutletContext<LayoutContextType>();
  const { translations = {} } = useContext(LanguageContext) || {};

  // ———————— Hooks hep en üstte ————————
  const [config, setConfig] = useState<PageConfig | null>(null);
  const [pendingFileCounts, setPendingFileCounts] = useState<Record<string, number>>({});
  const [fileNamesMap, setFileNamesMap] = useState<Record<string, string[]>>({});
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formUnused, setFormUnused] = useState(false);
  const [preApproval, setPreApproval] = useState(false);
  const [showPreApprovalContentModal, setShowPreApprovalContentModal] = useState(false);
  const [formUnusedMessage, setFormUnusedMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { id: formId, guid: formToken } = useParams();
  const [response, setResponse] = useState<FormDetailResponse | null>(null);

  const fetchClientTrack = async () => {
    if (!formId || !formToken) {
      console.error("Form ID veya Form Token bulunamadı.");
      return;
    }

    try {
      const res = await ClientTrackApi.getFormDetail(Number(formId), formToken);
      if (res && res?.responseCode == ResponseCode.Unused) {
        setFormUnused(true);
        setFormUnusedMessage(res.text);
      } else {
        setFormUnused(false);
        setFormUnusedMessage(null);
      }

      if (res && res.content) {
        const data: FormDetailResponse = res.content;

        // formComponents string olduğu için parse et
        const parsedFormComponents: Array<Record<string, any>> = JSON.parse(data.formComponents || "[]");

        // initialValues objesini oluştur
        const initialValues = parsedFormComponents.reduce((acc, field) => {
          if (!field?.id) return acc;

          if (field.type === ElementType.FILEUPLOADER) {
            acc[field.id] = [];
          } else if (field.type === ElementType.SELECT) {
            acc[field.id] = field.multiple ? [] : "";
          } else {
            acc[field.id] = "";
          }
          return acc;
        }, {} as Record<string, any>);

        setResponse(data);
        setConfig({
          rawFormElements: parsedFormComponents,
          confirmLabel: data.saveButtonText || "Kaydet",
          initialValues,
        });
      }
    } catch (error) {
      console.error("Form detayları alınamadı:", error);
    }
  };

  useEffect(() => {
    fetchClientTrack();
  }, []);

  // ➊ “API” çağrısı (örnek veri)
  //   useEffect(() => {
  //     const data: PageConfig = {
  //       rawFormElements: [
  //         {
  //           id: "nameSurname",
  //           type: "input",
  //           label: "Adınız ve Soyadınız99",
  //           rowId: 1,
  //           colClass: "col-6",
  //           inputType: "text",
  //           required: true,
  //         },
  //         {
  //           id: "phoneNumber",
  //           type: "input",
  //           label: "Telefon Numaranız",
  //           rowId: 1,
  //           colClass: "col-6",
  //           inputType: "tel",
  //           required: true,
  //         },
  //         {
  //           id: "citizenRequest",
  //           type: "textarea",
  //           label: "Talebiniz",
  //           rowId: 1,
  //           colClass: "col-12",
  //           rows: 3,
  //           required: true,
  //         },
  //         {
  //           id: "fileUploader",
  //           type: "fileuploader",
  //           label: "Dosya Yükle",
  //           rowId: 1,
  //           colClass: "col-12",
  //           url: "https://ccapitest.albinasoft.com/api/Portal/UploadFile",
  //           multi: true,
  //           allowedTypes: "all",
  //           maxFile: 10,
  //           maxSize: 10485760,
  //         },
  //       ],
  //       initialValues: {
  //         nameSurname: "",
  //         phoneNumber: "",
  //         citizenRequest: "",
  //         fileUploader: [],
  //       },
  //       confirmLabel: "Talep Oluştur",
  //       showCancelButton: false,
  //       cancelLabel: "",
  //     };
  //     setConfig(data);
  //   }, [response]);

  // ➋ config yüklendiğinde uploader ve formValues map’lerini başlat
  useEffect(() => {
    if (!config) return;
    const fileUploaderIds = config.rawFormElements
      .filter((el) => el.type === ElementType.FILEUPLOADER)
      .map((el) => el.id as string);

    const countsInit = fileUploaderIds.reduce((acc, id) => ({ ...acc, [id]: 0 }), {});
    const namesInit = fileUploaderIds.reduce((acc, id) => ({ ...acc, [id]: [] as string[] }), {});

    setPendingFileCounts(countsInit);
    setFileNamesMap(namesInit);
    setFormValues(config.initialValues);
  }, [config]);

  // ➌ sayfa meta ve layout atamaları (hook hep çağrılıyor; içerde config kontrol ediliyor)
  useEffect(() => {
    if (!response) return;
    setPageTitle(response.pageTitle);
    setPageSummary("");
    setVideoHelpUrl(response.helpLink);
    setShowHeader(true);
    setShowSidebar(false);
    setShowSummaryOnMobile(false);
    setLayoutHeaderImage(response.clientBannerUrl);
  }, [config]);

  // —————— Erken dönüş artık HOOK’lardan sonra yalnızca JSX için ——————
  if (formUnused) {
    return (
      <div>
        <h5 className="text-center">{formUnusedMessage}</h5>
      </div>
    );
  }

  if (!config) {
    return <div>Yükleniyor…</div>;
  }

  // ➍ config’den gelenleri ayıkla
  const { rawFormElements, confirmLabel } = config;

  // ➎ generic handler fabrikaları
  //   const handleFilesUploaded = (id: string) => (uploaded: string[]) => {
  //     setFileNamesMap((prev) => ({ ...prev, [id]: [...prev[id], ...uploaded] }));
  //     setFormValues((prev) => ({
  //       ...prev,
  //       [`fileNames_${id}`]: [...((prev[`fileNames_${id}`] as string[]) || []), ...uploaded],
  //     }));
  //   };

  const handleFilesUploaded = (id: string) => (uploaded: string[]) => {
    setFileNamesMap((prev) => ({ ...prev, [id]: [...prev[id], ...uploaded] }));
    setFormValues((prev) => ({
      ...prev,
      [id]: [...prev[id], ...uploaded],
      ["uploadedFileNames"]: [...((prev["uploadedFileNames"] as string[]) || []), ...uploaded],
    }));
  };

  const handleRemoveUploaded = (id: string) => (name: string) => {
    setFileNamesMap((prev) => ({ ...prev, [id]: prev[id].filter((n) => n !== name) }));
    setFormValues((prev) => ({
      ...prev,
      [`fileNames_${id}`]: ((prev[`fileNames_${id}`] as string[]) || []).filter((n) => n !== name),
    }));
  };

  const handlePendingChange = (id: string) => (count: number) => {
    setPendingFileCounts((prev) => ({ ...prev, [id]: count }));
  };

  const handleChange = (id: string) => (e: Date | null | React.ChangeEvent<any>) => {
    if (e instanceof Date || e === null) {
      setFormValues((prev) => ({ ...prev, [id]: e }));
    } else if (e && typeof e === "object" && "target" in e) {
      const { type, checked, value } = e.target as HTMLInputElement;
      setFormValues((prev) => ({
        ...prev,
        [id]: type === "checkbox" ? checked : value,
      }));
    } else {
      setFormValues((prev) => ({ ...prev, [id]: e }));
    }
  };

  // ➏ her elemana handler’ları iliştir
  const formElements = rawFormElements
    .filter((el) => !el.isAdminOnly)
    .map((el) => {
      if (el.type === ElementType.FILEUPLOADER) {
        return {
          ...el,
          onUploadComplete: handleFilesUploaded(el.id),
          onRemoveUploaded: handleRemoveUploaded(el.id),
          onPendingChange: handlePendingChange(el.id),
          id: el.id,
          type: el.type,
          url: `${GlobalOperations.BaseUrl}/Portal/UploadFile`,
          multi: el.multi,
          allowedTypes: el.allowedTypes,
          maxFile: el.maxFile,
          maxSize: el.maxSize,
          rowId: el.rowId,
          colClass: el.colClass,
        };
      }

      // SELECT için: doğru boş değer ve placeholder
      if (el.type === ElementType.SELECT) {
        const safePlaceholder =
          el.placeholder && typeof el.placeholder === "string" && el.placeholder.trim() !== ""
            ? el.placeholder
            : "Seçiniz...";

        const emptyValue = el.multiple ? [] : "";

        return {
          ...el,
          placeholder: safePlaceholder,
          value: formValues[el.id] ?? emptyValue,
          onChange: handleChange(el.id),
          id: el.id,
          type: el.type,
          rowId: el.rowId,
          colClass: el.colClass,
        };
      }

      // Diğer alanlar
      return {
        ...el,
        value: formValues[el.id] ?? "",
        onChange: handleChange(el.id),
        id: el.id,
        type: el.type,
        rowId: el.rowId,
        colClass: el.colClass,
      };
    });

  // ➐ form submit
  const handleSubmit = async (_values: Record<string, any>) => {
    if (!formId || !formToken) {
      console.error("Form ID veya Form Token bulunamadı.");
      return;
    } else {
      const formDataList = Object.entries(formValues).map(([id, value]) => ({
        id,
        value,
      }));

      const requestPayload: SaveFormDataRequest = {
        formData: JSON.stringify(formDataList),
        uploadedFileNames: formValues.uploadedFileNames,
      };
      try {
        const result = await ClientTrackApi.saveFormData(Number(formId), formToken, requestPayload);
        if (result?.responseCode === 200) {
          toast.success(result?.text || "Talebiniz başarıyla oluşturuldu.");
          setFormValues(config?.initialValues || {});
          setFormSubmitted(true); // Form kartını gizlemek için
          setSuccessMessage(result?.content?.successMessage || ""); // Üst mesajı değiştirmek için
        } else {
          toast.error(result?.text || "Form gönderimi başarısız.");
        }
      } catch (err) {
        console.error("Gönderim hatası:", err);
        toast.error("Sunucu hatası oluştu.");
      }
    }
  };

  return (
    <div className="content-inner container-fluid pb-0">
      <Helmet>
        <title>{response?.pageTitle}</title>
      </Helmet>

      {/* CASE 1: PreApproval varsa */}
      {response?.preApprovalHeader ? (
        <>
          {/* Checkbox Card */}
          {!preApproval && (
            <div className="card">
              <div className="card-body">
                <h6 style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={preApproval}
                    onChange={(e) => setPreApproval(e.target.checked)}
                    style={{ marginRight: "0.5rem" }}
                  />
                  <span
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => setShowPreApprovalContentModal(true)}
                  >
                    {response?.preApprovalHeader}
                  </span>
                </h6>
              </div>
            </div>
          )}

          {/* Form (checkbox işaretlendikten sonra gösterilecek) */}
          {preApproval && !formSubmitted && response?.pageDescription && (
            <div className="card">
              <div className="card-body">
                <h6>
                  {response?.pageDescription?.split("\n").map((line, index) => (
                    <span key={index}>
                      {line}
                      <br />
                    </span>
                  ))}
                </h6>
              </div>
            </div>
          )}

          {preApproval && !formSubmitted && (
            <div className="card">
              <div className="card-body">
                <CustomForm
                  elements={formElements}
                  onSubmit={handleSubmit}
                  handleCancel={() => {}}
                  showConfirmButton={true}
                  confirmLabel={confirmLabel}
                  innerRowCustomClass=""
                  onNotify={onNotify}
                />
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="card">
              <div className="card-body">
                <h6>{successMessage}</h6>
              </div>
            </div>
          )}
        </>
      ) : (
        /* CASE 2: PreApproval yoksa direkt form + success message */
        <>
          {!formSubmitted && (
            <>
              <div className="card">
                <div className="card-body">
                  <h6>
                    {response?.pageDescription?.split("\n").map((line, index) => (
                      <span key={index}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </h6>
                </div>
              </div>
              <div className="card">
                <div className="card-body">
                  <CustomForm
                    elements={formElements}
                    onSubmit={handleSubmit}
                    handleCancel={() => {}}
                    showConfirmButton={true}
                    confirmLabel={confirmLabel}
                    innerRowCustomClass=""
                    onNotify={onNotify}
                  />
                </div>
              </div>
            </>
          )}

          {successMessage && (
            <div className="card">
              <div className="card-body">
                <h6>{successMessage}</h6>
              </div>
            </div>
          )}
        </>
      )}

      <CustomModal
        title="Ön Onay Metni"
        size="xl"
        show={showPreApprovalContentModal}
        onHide={() => {
          setShowPreApprovalContentModal(false);
        }}
      >
        <h6>
          {response?.preApprovalContent?.split("\n").map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </h6>
      </CustomModal>
    </div>
  );
};

export default RequestCreationForm;
